<script setup lang="ts">
import type { BlockUser } from '~/interfaces/app';
import RenderHTML from '~/components/ui/RenderHTML.vue';

defineProps({
  num: {
    type: String,
    default: '',
  },
  block: {
    type: Object as () => null | BlockUser,
    default: null,
  },
  index: {
    type: String,
    default: '',
  },
  partCount: {
    type: Number,
    default: 1,
  },
  renderHtml: {
    type: Boolean,
    default: false,
  },
});
</script>

<template>
  <li v-if="block" :class="['content-list', `part-${partCount}`]">
    <div class="content-flex">
      <span class="content-number">{{ num + index }}</span>
      <RenderHTML
        v-if="renderHtml"
        tag-wrapper="span"
        :seotext="block.text"
        class-wrapper="block-text-position"
      />
      <span v-else>{{ block.text }}</span>
    </div>
    <div v-if="block.tableData" class="table-data">
      <table>
        <thead>
          <tr>
            <th
              v-for="(head, i) in block.tableData.titleList"
              :key="`head_${i}`"
            >
              {{ head }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, ii) in block.tableData.textList"
            :key="`item_${ii}`"
          >
            <td align="center">{{ item.category }}</td>
            <td>
              <ul>
                <li v-for="(value, iii) in item.list" :key="iii">
                  {{ value }}
                </li>
              </ul>
            </td>
            <td>{{ item.count }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <ol v-if="block.list && block.list.length">
      <content-user
        v-for="(subBlock, listIndex) in block.list"
        :key="`${listIndex}_${index}`"
        :num="`${num.slice(0, -1)}.${index}.`"
        :index="`${listIndex + 1}`"
        :render-html="renderHtml"
        :block="subBlock"
        :part-count="partCount + 1"
      />
    </ol>
  </li>
</template>

<style scoped lang="scss">
ul li {
  margin-bottom: 8px;

  @include xl-block {
    margin-bottom: 12px;
  }
}

.content-number {
  display: inline-block;

  width: 32px;
  min-width: 32px;

  @include xl-block {
    width: 44px;
    min-width: 44px;
  }
}

.part-2 > .content-flex {
  font-size: 20px;
  font-weight: bold;
}

.part-4 .content-number {
  width: 44px;
  min-width: 44px;

  @include xl-block {
    width: 56px;
    min-width: 56px;
  }
}

.part-5 .content-number {
  width: 56px;
  min-width: 56px;

  @include xl-block {
    width: 68px;
    min-width: 68px;
  }
}

.content-flex {
  display: flex;
  margin-bottom: 8px;

  @include xl-block {
    margin-bottom: 12px;
  }
}

.part-3,
.part-4,
.part-5 {
  margin-left: 32px;

  @include xl-block {
    margin-left: 44px;
  }
}

.table-data {
  width: 100%;
  overflow-x: auto;
}

table {
  border: 1px solid $achromatic-black;
  width: 100%;

  th {
    background-color: $achromatic-star;
    font-weight: bold;
    text-align: center;
  }

  ul {
    text-align: left;
  }

  th,
  td {
    padding: 10px;
    align-items: center;
    border: 1px solid $achromatic-black;
    vertical-align: middle;
  }
}

.block-text-position {
  margin-left: 1em;
}
</style>
